import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, School, CheckSquare, TrendingUp, Clock, Calendar,
  AlertCircle, DollarSign, Utensils, MapPin, Award,
  Activity, FileText, Settings, Bell
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { lookupDataAPI, staffAttendanceAPI, mealRequestsAPI, calendarAPI } from '../../services/api';

const StaffDashboard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [stats, setStats] = useState({
    totalSchools: 0,
    totalStudents: 0,
    todayMeals: 0,
    monthlyMeals: 0,
    attendanceRate: 0,
    pendingTasks: 0,
    budgetUsed: 0,
    activeBranches: 0,
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load dashboard stats
      const [lookupData, attendanceData, mealData] = await Promise.all([
        lookupDataAPI.getLookupData(),
        staffAttendanceAPI.getTodayAttendance(),
        mealRequestsAPI.getRequests()
      ]);

      // Set stats from real data
      setStats({
        totalSchools: lookupData.schools?.length || 0,
        totalStudents: lookupData.schools?.reduce((sum, school) => sum + (school.total_students || 0), 0) || 0,
        todayMeals: mealData?.filter(m => m.date === new Date().toISOString().split('T')[0]).reduce((sum, m) => sum + (m.total_meals || 0), 0) || 0,
        monthlyMeals: lookupData.dashboard_stats?.meals_count || 0,
        attendanceRate: attendanceData?.is_clocked_in ? 100 : 0, // Simplified for now
        pendingTasks: 0, // Tasks API not implemented yet
        budgetUsed: 0, // Budget tracking not implemented yet
        activeBranches: lookupData.branches?.length || 0,
      });

      // Set current date
      setCurrentDate(new Date().toLocaleDateString());

      // Set recent activities from real data
      const activities = [
        ...(mealData?.slice(0, 3).map(meal => ({
          id: meal.id,
          type: 'meal',
          message: `Meal request for ${meal.school_name || 'Unknown School'} - ${meal.date}`,
          time: new Date(meal.created_at).toLocaleString(),
          user: user?.full_name || 'You',
          status: 'success'
        })) || []),
      ];
      if (attendanceData?.is_clocked_in) {
        activities.unshift({
          id: 'attendance',
          type: 'attendance',
          message: `Clocked in at ${new Date(attendanceData.clock_in_time).toLocaleTimeString()}`,
          time: new Date(attendanceData.clock_in_time).toLocaleString(),
          user: user?.full_name || 'You',
          status: 'success'
        });
      }
      setRecentActivities(activities);

      // Set upcoming events (placeholder for now)
      setUpcomingEvents([
        {
          id: 1,
          title: 'Daily Attendance Check',
          date: new Date().toLocaleString(),
          type: 'attendance',
          attendees: 1
        }
      ]);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data', 'Please try again');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Schools',
      value: stats.totalSchools,
      icon: School,
      color: 'blue',
      change: '+5%',
      changeType: 'positive',
      description: 'Active schools this month'
    },
    {
      title: 'Students Fed',
      value: stats.totalStudents.toLocaleString(),
      icon: Users,
      color: 'green',
      change: '+12%',
      changeType: 'positive',
      description: 'Total beneficiaries'
    },
    {
      title: "Today's Meals",
      value: stats.todayMeals.toLocaleString(),
      icon: Utensils,
      color: 'orange',
      change: '+8%',
      changeType: 'positive',
      description: 'Meals served today'
    },
    {
      title: 'Attendance Rate',
      value: `${stats.attendanceRate}%`,
      icon: Activity,
      color: 'purple',
      change: '+2.3%',
      changeType: 'positive',
      description: 'Staff attendance'
    }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'meal': return Utensils;
      case 'attendance': return CheckSquare;
      case 'alert': return AlertCircle;
      case 'task': return FileText;
      default: return Clock;
    }
  };

  const getActivityColor = (status) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-600 border-green-200';
      case 'warning': return 'bg-amber-100 text-amber-600 border-amber-200';
      case 'error': return 'bg-red-100 text-red-600 border-red-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'meeting': return 'bg-blue-100 text-blue-600';
      case 'inspection': return 'bg-purple-100 text-purple-600';
      case 'event': return 'bg-green-100 text-green-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Welcome back, {user?.full_name}! 👋
            </h1>
            <p className="text-gray-600 mt-2">
              Here's your overview for today, {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <Badge variant="success" className="text-sm px-3 py-1">
              Online
            </Badge>
            <Button variant="outline" size="sm">
              <Bell size={16} className="mr-2" />
              Notifications (3)
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card hover className="relative overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                      <Icon className={`text-${stat.color}-600`} size={24} />
                    </div>
                    <div className={`text-sm font-medium ${
                      stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {stat.change}
                    </div>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-sm font-medium text-gray-900 mt-1">{stat.title}</p>
                    <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                  </div>
                </CardContent>
                {/* Decorative gradient */}
                <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-${stat.color}-50 to-transparent rounded-bl-full opacity-50`} />
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-6">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {recentActivities.map((activity, index) => {
                  const Icon = getActivityIcon(activity.type);
                  return (
                    <motion.div
                      key={activity.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.6 + index * 0.1 }}
                      className="flex items-start space-x-3 p-2 rounded-lg hover:bg-gray-50"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.status)}`}>
                        <Icon size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{activity.message}</p>
                        <div className="flex items-center mt-1 text-xs text-gray-500">
                          <span>{activity.user}</span>
                          <span className="mx-1">•</span>
                          <span>{activity.time}</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Additional Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Events */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Events</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {upcomingEvents.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.7 + index * 0.1 }}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-3">
                      <Calendar className="text-gray-400" size={20} />
                      <div>
                        <p className="font-medium text-gray-900">{event.title}</p>
                        <p className="text-sm text-gray-500">{event.date}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="info" className={getEventColor(event.type)}>
                        {event.type}
                      </Badge>
                      <span className="text-sm text-gray-500">{event.attendees} attendees</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Performance Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Performance Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Monthly Budget Used</span>
                    <span className="text-sm font-bold text-orange-600">{stats.budgetUsed}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-orange-500 to-amber-400 h-2 rounded-full"
                      style={{ width: `${stats.budgetUsed}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <DollarSign className="text-blue-600 mx-auto mb-1" size={20} />
                    <p className="text-lg font-bold text-gray-900">₹2.3M</p>
                    <p className="text-xs text-gray-600">Budget Remaining</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <MapPin className="text-green-600 mx-auto mb-1" size={20} />
                    <p className="text-lg font-bold text-gray-900">{stats.activeBranches}</p>
                    <p className="text-xs text-gray-600">Active Branches</p>
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Pending Tasks</span>
                    <Badge variant="warning">{stats.pendingTasks}</Badge>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-gray-600">Monthly Meals</span>
                    <span className="text-sm font-bold text-gray-900">{stats.monthlyMeals.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default StaffDashboard;
